import { Spin } from 'aa_common/front-end/antd'
import { If } from 'aa_common/front-end/components'
import { defaultTo, everyTrue, ifElse, isNumberHaveValue, someTrue } from 'aa_common/front-end/helpers'
import { Button } from 'antd'
import * as assetApi from 'api/app/asset'
import * as depreciationRatesApi from 'api/app/depreciationRates'
import { DATE_FORMAT, DEPRECIATION_METHOD_CODE, EXCISE_TAX_METHOD, LEDGER_PURPOSE } from 'common/constants'
import { isAccountingJournalTarget, isTaxJournalTarget } from 'common/helpers/ledger-helper'
import { getNs } from 'common/i18n-config'
import { Asset, AssetLedger, LedgerSetting } from 'common/models'
import { PresenterFixedAssetDepreciationResponse } from 'common/open-api'
import { FeatureFlagNames } from 'common/open-features'
import { useCheckFlag } from 'common/open-features/hooks'
import { useActionsStatus } from 'containers/assets/asset-list-container/hooks/use-actions-status'
import useResource from 'lib/hooks/useResource'
import { get, isEqual } from 'lodash'
import last from 'lodash/last'
import moment from 'moment'
import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { useSelector } from 'react-redux'
import { useLocation } from 'react-router-dom'
import { selectMonthlyClosingCurrent } from 'store/accounting/monthlyClosing/selectors'
import { selectEventHistories } from 'store/asset/selectors'
import { getExciseSettingsByDateList } from 'store/settings/excise/selectors'
import { selectLedgerSettingList } from 'store/settings/ledgerSetting/selectors'

import { AcceleratedDepreciationDrawer } from './components/accelerated-depreciation-drawer'
import AmortizationInfoTable from './components/AmortizationInfoTable'
import { isChangeDepreciationMethod } from './components/AmortizationInfoTable/tables/helper'
import { DepreciationAdjustmentDropdown } from './components/depreciation-adjustment-dropdown'
import DepreciationTable from './components/DepreciationTable'
import { DepreciationResults } from './components/DepreciationTable/model'
import { useAcceleratedDepreciationDrawer } from './hooks/use-accelerated-depreciation-drawer'
import { Section, SectionHeaderText, StyledSectionHeader, Wrapper, WrapperLedger } from './styles'

interface IAmortization {
  data: Asset
  changeToTab?: (tab: string) => void
}

const { t, useNsReady } = getNs('components/organisms/assets/asset-detail')
const Amortization = ({ data, changeToTab }: IAmortization) => {
  useNsReady()
  const { isEnable: enablePartialImpairment } = useCheckFlag(FeatureFlagNames.PartialImpairment)
  const { state }: any = useLocation()
  const [selectedAssetLedger, setSelectedAssetLedger] = useState<AssetLedger | null>(null)
  const ledgerSettings: LedgerSetting[] = useSelector(selectLedgerSettingList, isEqual)
  const currentMonthlyClosing = useSelector(selectMonthlyClosingCurrent, isEqual)
  const { data: exciseSetting } = useSelector(getExciseSettingsByDateList, isEqual)

  const isTaxMethodExcluded = exciseSetting?.tax_method === EXCISE_TAX_METHOD.EXCLUDED_TAX

  const hasUsageStarted = !!data.usage_started_at

  const [depreciationResults, getDepreciationResults] = useResource(assetApi.getDepreciationResults)

  const [depreciationRate, getDepreciationRate] = useResource(depreciationRatesApi.get)
  const [depreciationRateData, setRenderDepreciationRate] = useState(null)
  const [isShowComparisonTable, setShowComparisonTable] = useState<boolean>(false)
  const [isShowAcceleratedDepreciationDrawer, setShowAcceleratedDepreciationDrawer] = useState(false)

  const eventHistories = useSelector(selectEventHistories, isEqual)
  const lastRecord = useMemo(() => last(eventHistories), [eventHistories])
  const dividedAts: string[] = useMemo(() => {
    const tmp: string[] = []
    eventHistories?.forEach(eventHistory => {
      const dividedAt = get(eventHistory, 'divided_at', null)
      if (dividedAt) tmp.push(dividedAt as string)
    })
    return tmp
  }, [eventHistories])
  const isSourceAsset = !get(lastRecord, 'dividing_source_fixed_asset', null)

  const getLedgerByType = useCallback(
    (type: LEDGER_PURPOSE) => {
      return data?.fixed_asset_ledgers?.find(ledger => ledger?.ledger_setting?.ledger_purpose === type)
    },
    [data?.fixed_asset_ledgers]
  )

  const isShowComparisonButton = useMemo(() => {
    const taxLedger = getLedgerByType(LEDGER_PURPOSE.TAX)
    const accountingLedger = getLedgerByType(LEDGER_PURPOSE.ACCOUNTING)

    return !!(
      accountingLedger &&
      taxLedger &&
      taxLedger?.depreciation_method_code !== DEPRECIATION_METHOD_CODE.NON_DEPRECIABLE &&
      taxLedger?.depreciation_method_code !== DEPRECIATION_METHOD_CODE.NON_DEPRECIATION
    )
  }, [getLedgerByType])

  const isDeclineBalanceMethods = useMemo(() => {
    const taxLedger = getLedgerByType(LEDGER_PURPOSE.TAX)
    return !!(
      taxLedger?.depreciation_method_code &&
      [
        DEPRECIATION_METHOD_CODE.OLD_DECLINING_BALANCE,
        DEPRECIATION_METHOD_CODE.NEW_DECLINING_BALANCE_250,
        DEPRECIATION_METHOD_CODE.NEW_DECLINING_BALANCE_200,
      ].includes(taxLedger.depreciation_method_code)
    )
  }, [getLedgerByType])

  useEffect(() => {
    if (data.fixed_asset_ledgers?.length > 0) {
      setSelectedAssetLedger(prevState => {
        return ifElse(
          !prevState,
          data.fixed_asset_ledgers[0],
          defaultTo(
            data.fixed_asset_ledgers.find(
              assetLedger => assetLedger.ledger_setting_id === prevState?.ledger_setting_id
            ),
            null
          )
        )
      })
    }
  }, [data])

  const filteredLedgerSettings: LedgerSetting[] = useMemo(() => {
    if (!ledgerSettings || !data.fixed_asset_ledgers) return []

    const assetLedgerSettingIds: number[] = data.fixed_asset_ledgers.map((ledger: any) => {
      return ledger.ledger_setting_id
    })

    return ledgerSettings.filter(ledgerSetting => {
      return assetLedgerSettingIds.includes(ledgerSetting.id as number)
    })
  }, [ledgerSettings, data.fixed_asset_ledgers]) // eslint-disable-line

  const handleLedgerChange = (value: any) => {
    if (!value) return handleShowComparisonTable()

    const selectedLedger = data.fixed_asset_ledgers.find(assetLedger => assetLedger.ledger_setting_id === value) || null
    setShowComparisonTable(false)
    setSelectedAssetLedger(selectedLedger)
  }

  const fetchDepreciationResults = useCallback(
    (assetLedger: AssetLedger | null) => {
      if (data && assetLedger && !isShowComparisonTable) {
        getDepreciationResults(data.biid, assetLedger.ledger_setting_id)
      }
    },
    [getDepreciationResults, data, isShowComparisonTable]
  )

  useEffect(() => {
    fetchDepreciationResults(selectedAssetLedger)
  }, [fetchDepreciationResults, selectedAssetLedger])

  useEffect(() => {
    const depreciationRateValue = get(depreciationRate.data, '0.depreciation_rate')
    setRenderDepreciationRate(depreciationRateValue)
  }, [depreciationRate.data])

  const depreciationCostValue: number = useMemo((): number => {
    return isTaxMethodExcluded ? data?.acquisition_cost_excluded_excise : data?.acquisition_cost
  }, [data?.acquisition_cost, data?.acquisition_cost_excluded_excise])

  useEffect(() => {
    selectedAssetLedger &&
      !isShowComparisonTable &&
      getDepreciationRate({
        depreciation_method: ifElse(
          someTrue(!enablePartialImpairment, !selectedAssetLedger?.original_depreciation_method_code),
          selectedAssetLedger?.depreciation_method_code,
          selectedAssetLedger?.original_depreciation_method_code
        ),
        service_life: isChangeDepreciationMethod(selectedAssetLedger, depreciationCostValue)
          ? selectedAssetLedger?.service_life_for_depreciation_calc
          : ifElse(
              someTrue(!enablePartialImpairment, !isNumberHaveValue(selectedAssetLedger?.original_service_life)),
              selectedAssetLedger?.service_life,
              selectedAssetLedger?.original_service_life
            ),
      })
  }, [
    getDepreciationRate,
    selectedAssetLedger,
    isShowComparisonTable,
    isChangeDepreciationMethod,
    enablePartialImpairment,
  ])

  useEffect(() => {
    const underOverDepreciationReportPath = '/depreciation/under-over-depreciation-report'
    if (state?.fromPath === underOverDepreciationReportPath) {
      setShowComparisonTable(true)
    }
  }, [state])

  const memoizedDepreciationResults = useMemo(() => {
    return (depreciationResults.data || { data: [], terms: [] }) as DepreciationResults
  }, [depreciationResults.data])

  const handleShowComparisonTable = () => {
    setShowComparisonTable(true)
  }

  const isAccountantLedger = isAccountingJournalTarget(selectedAssetLedger?.ledger_setting)
  const { drawerAssetLedger, drawerDepreciationResult } = useAcceleratedDepreciationDrawer(
    selectedAssetLedger,
    memoizedDepreciationResults,
    isShowAcceleratedDepreciationDrawer
  )

  const { getActionsStatus, findActionsStatusByBiid } = useActionsStatus()
  const refreshAfterUpdateAccelerated = useCallback(() => {
    getActionsStatus([data.biid!])
    fetchDepreciationResults(selectedAssetLedger)
  }, [getActionsStatus, fetchDepreciationResults, selectedAssetLedger, data])

  const actionsStatusByBiid = defaultTo(findActionsStatusByBiid(data.biid as string), {})

  return (
    <Wrapper>
      <If condition={hasUsageStarted}>
        <Spin loading={depreciationResults.isLoading}>
          <WrapperLedger>
            <If condition={filteredLedgerSettings.length > 0}>
              {filteredLedgerSettings.map(({ id, name }) => (
                <Button
                  key={id}
                  className={ifElse(
                    selectedAssetLedger?.ledger_setting_id === id && !isShowComparisonTable,
                    'active',
                    'disable'
                  )}
                  onClick={() => handleLedgerChange(id)}
                  shape="round"
                >
                  {name}
                </Button>
              ))}
            </If>

            <If condition={isShowComparisonButton}>
              <Button
                className={isShowComparisonTable ? 'active' : 'disable'}
                onClick={handleShowComparisonTable}
                shape="round"
              >
                {t('depreciation_comparison')}
              </Button>
            </If>
          </WrapperLedger>

          <Section>
            <StyledSectionHeader>
              <div>
                <If condition={!isShowComparisonTable}>
                  <SectionHeaderText>{t('depreciation_method')}</SectionHeaderText>
                  <SectionHeaderText>{`${moment(data.usage_started_at).format(DATE_FORMAT)}〜`}</SectionHeaderText>
                </If>
              </div>
              <If condition={!isShowComparisonTable && isAccountantLedger}>
                <DepreciationAdjustmentDropdown
                  assetLedger={selectedAssetLedger as AssetLedger}
                  depreciationResults={memoizedDepreciationResults as PresenterFixedAssetDepreciationResponse}
                  onOpen={() => setShowAcceleratedDepreciationDrawer(true)}
                  onCancelSuccess={refreshAfterUpdateAccelerated}
                  changeToEventTab={() => changeToTab?.('3')}
                  isShowAcceleratedDepreciationDrawer={isShowAcceleratedDepreciationDrawer}
                />
              </If>
            </StyledSectionHeader>

            <If condition={selectedAssetLedger}>
              <AmortizationInfoTable
                ledger={selectedAssetLedger as AssetLedger}
                depreciationRate={depreciationRateData}
                biid={data?.biid}
                isShowComparisonTable={isShowComparisonTable}
                acquisitionCost={depreciationCostValue}
              />
            </If>
          </Section>

          {!isShowComparisonTable && (
            <Section>
              <SectionHeaderText>{t('depreciation_calculation')}</SectionHeaderText>
              <DepreciationTable
                dividedAts={dividedAts}
                isSourceAsset={isSourceAsset}
                acquiredAt={data.acquired_at as string}
                usageStartedAt={data.usage_started_at as string}
                retiredAt={data.retired_at as string}
                soldAt={data.sold_at as string}
                depreciationResults={memoizedDepreciationResults}
                currentMonthlyClosing={currentMonthlyClosing}
                impairAt={data.impaired_at as string}
                isAccountLedger={isAccountingJournalTarget(selectedAssetLedger?.ledger_setting)}
                isShowComparison={isShowComparisonButton && isTaxJournalTarget(selectedAssetLedger?.ledger_setting)}
                isDeclineBalanceMethods={isDeclineBalanceMethods}
                showComparisonTable={handleShowComparisonTable}
                isAccelerated={actionsStatusByBiid?.adjusted}
                showImpairmentInfo={everyTrue(
                  !!enablePartialImpairment,
                  !!data.impaired_at,
                  isAccountingJournalTarget(selectedAssetLedger?.ledger_setting)
                )}
              />
            </Section>
          )}
        </Spin>
      </If>

      <If condition={!hasUsageStarted}>
        <span>{t('depreciation_calculation_not_begin_msg')}</span>
      </If>

      <If condition={isShowAcceleratedDepreciationDrawer}>
        <AcceleratedDepreciationDrawer
          assetLedger={drawerAssetLedger}
          depreciationResults={drawerDepreciationResult}
          onModalClose={() => setShowAcceleratedDepreciationDrawer(false)}
          onRegisterSuccess={refreshAfterUpdateAccelerated}
        />
      </If>
    </Wrapper>
  )
}

export default Amortization
